import { request } from '@/lib/api'

/**
 * Доступ к счетам, разнесению оплат и дебиторке (тикет #72, ТЗ §4.3).
 *
 * ## Почему отдельно от `card/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: он читает и пишет **одну плоскую
 * строку** через механизм списков и не знает, какая это сущность. Счёт плоской строкой не
 * описывается: у него есть позиции, у позиций — построчный НДС, а итог обязан быть суммой
 * строк. Пытаться собрать его из колонок списка значило бы показать `total` рядом с
 * `vat_total` и умолчать о том, из чего они сложились, — то есть показать бухгалтеру
 * цифру, которую он не может проверить.
 *
 * ## Почему счёт не редактируется через каркас
 *
 * Итоги счёта **не поля формы**: `subtotal`, `vat_total` и `total` пересчитываются
 * доменом при каждой правке позиций (`InvoiceService.addLine`), и разрешить править их
 * напрямую значило бы позволить сохранить счёт, чей итог не сходится со своими строками.
 * Поэтому строки добавляются и удаляются **операциями**, а не сохранением формы. Сервер
 * это и обеспечивает: `bulk/update` принимает только колонки из `importable`, а счёт не
 * объявляет ни одной.
 *
 * ## Чего здесь нет — названо вслух
 *
 * - **Повторная выписка счёта.** Домен её не публикует вовсе — см. `InvoiceTabs`.
 * - **Правка шаблона строк подписки и выставление по одной подписке**
 *   (`POST /subscriptions/{id}/template`, `/bill`). Эндпоинты есть, обёрток нет: карточка
 *   подписки (#94) управляет её жизнью — остановить, возобновить, — а шаблон и внеочередное
 *   выставление остаются за API. Обёртка без экрана выглядела бы рабочей возможностью.
 *
 * Приостановка и возобновление подписки (#94) — карточка подписки
 * (`SubscriptionRecordView`, `/subscriptions/:id`): подписка объявлена в каталоге списков
 * как `billing.recurring` под правом `billing.invoice`.
 */

// ------------------------------------------------------------------ счёт

/**
 * Строка счёта.
 *
 * `vatRate` — процент (20), а не доля: так задано в домене и в схеме
 * (`invoice_item.vat_rate numeric(5,2)`). Перевод в долю на клиенте завёл бы второе
 * место, где решается, что такое «двадцать процентов».
 */
export interface InvoiceLine {
  id: string
  productId: string | null
  description: string
  uomId: string | null
  quantity: number
  unitPrice: number
  discountPercent: number
  vatRate: number
  vatAmount: number
  lineTotal: number
  lineOrder: number
}

/**
 * Счёт целиком.
 *
 * `outstanding` приходит **с сервера**, хотя это разность `total - paidTotal`. Считать её
 * здесь значило бы завести второй ответ на вопрос «сколько ещё должны»: домен решает, что
 * входит в остаток отменённого счёта, и повторённая на клиенте формула разошлась бы с ним
 * молча — в ту сторону, где долг выглядит меньше.
 *
 * `payable` тоже с сервера: «можно ли на этот счёт разносить оплату» зависит от статуса и
 * остатка вместе, и своя проверка на клиенте либо предложит разнести на отменённый счёт,
 * либо запретит там, где домен разрешает.
 */
export interface Invoice {
  id: string
  number: string
  status: string
  organizationId: string
  counterpartyId: string
  dealId: string | null
  contractId: string | null
  issuedOn: string
  dueOn: string | null
  currency: string
  subtotal: number
  discountTotal: number
  vatTotal: number
  total: number
  paidTotal: number
  outstanding: number
  payable: boolean
  sentAt: string | null
  paidAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
  publicToken: string | null
  /** `null` в списках: коллекция ленивая, и сервер не разворачивает её ради строки. */
  lines: InvoiceLine[] | null
}

/** Платёж — тот же вид, что отдаёт #67. */
export interface Payment {
  id: string
  number: string | null
  direction: string
  method: string
  status: string
  organizationId: string
  counterpartyId: string | null
  bankAccountId: string | null
  amount: number
  currency: string
  unallocatedAmount: number
  paymentDate: string
  valueDate: string | null
  purpose: string | null
  payerName: string | null
  payerInn: string | null
  payerAccount: string | null
  externalId: string | null
  reversesPaymentId: string | null
  hasAdvance: boolean
  allocatable: boolean
}

/** Разнесение: сколько этого платежа ушло на этот счёт. */
export interface Allocation {
  id: string
  paymentId: string
  invoiceId: string
  amount: number
  rule: string
  allocatedAt: string
  allocatedBy: string | null
}

/**
 * Разнесение вместе с платежом, который его сделал.
 *
 * `payment == null` — порча данных: строка ссылается на платёж, которого нет. Показывать
 * это надо пустотой, а не выдуманным номером, но и падать нельзя — одна битая строка
 * иначе закроет бухгалтеру весь счёт.
 */
export interface Settlement {
  allocation: Allocation
  payment: Payment | null
}

/**
 * Ageing **одной валюты**.
 *
 * Та же форма, что на карточке контрагента (#70), и намеренно не общий тип: там она часть
 * финансовой сводки, здесь — самостоятельный отчёт. `current` — это «срок не наступил», а
 * не «текущая задолженность»: счёт, выставленный вчера со сроком через месяц, не
 * просрочка, и сложить его с просроченным значит показать долг хуже, чем он есть.
 */
export interface AgeingLine {
  counterpartyId: string
  currency: string
  current: number
  upTo30Days: number
  upTo60Days: number
  upTo90Days: number
  beyond90Days: number
}

/** Ступень эскалации: одна строка `dunning_action`. */
export interface DunningAction {
  id: string
  invoiceId: string
  daysOverdue: number
  kind: string
  scheduledOn: string
  executedAt: string | null
  result: string | null
  executed: boolean
}

/** Дебиторка контрагента: сумма и неоплаченные счета, старые первыми. */
export interface Receivable {
  counterpartyId: string
  receivable: number
  outstanding: Invoice[]
}

/** Подписка: расписание и ближайшее списание. */
export interface Subscription {
  id: string
  organizationId: string
  counterpartyId: string
  contractId: string | null
  name: string
  frequency: string
  dayOfMonth: number
  startDate: string
  endDate: string | null
  nextRunOn: string | null
  amount: number | null
  currency: string
  active: boolean
  template: unknown
}

const BASE = '/billing'

// ------------------------------------------------------------------ чтение

export function fetchInvoice(id: string, signal?: AbortSignal): Promise<Invoice> {
  return request<Invoice>(`${BASE}/invoices/${id}`, { signal })
}

/**
 * Чем закрыт счёт.
 *
 * Отдельный запрос, а не поле счёта: он требует **другого права**
 * (`payments.payment:read`), потому что несёт платёжные документы целиком. Вложив его в
 * счёт, мы бы отдали выписку каждому, кому выдали счета на просмотр.
 */
export function fetchSettlements(id: string, signal?: AbortSignal): Promise<Settlement[]> {
  return request<Settlement[]>(`${BASE}/invoices/${id}/settlements`, { signal })
}

/**
 * Лента напоминаний по счёту.
 *
 * **Все** пройденные ступени, а не последняя: обход записывает каждую пропущенную, и
 * показать только свежую значит скрыть, что клиенту уже писали трижды. Именно история
 * ступеней объясняет, почему счёт сегодня у юристов.
 */
export function fetchDunning(id: string, signal?: AbortSignal): Promise<DunningAction[]> {
  return request<DunningAction[]>(`${BASE}/invoices/${id}/dunning`, { signal })
}

export function fetchAgeing(counterpartyId: string, signal?: AbortSignal): Promise<AgeingLine[]> {
  return request<AgeingLine[]>(`${BASE}/counterparties/${counterpartyId}/ageing`, { signal })
}

export function fetchReceivable(
  counterpartyId: string,
  signal?: AbortSignal,
): Promise<Receivable> {
  return request<Receivable>(`${BASE}/counterparties/${counterpartyId}/receivable`, { signal })
}

export function fetchSubscriptionsOf(
  counterpartyId: string,
  signal?: AbortSignal,
): Promise<Subscription[]> {
  return request<Subscription[]>(`${BASE}/counterparties/${counterpartyId}/subscriptions`, {
    signal,
  })
}

/** Подписка с разобранным шаблоном строк (#94). */
export function fetchSubscription(id: string, signal?: AbortSignal): Promise<Subscription> {
  return request<Subscription>(`${BASE}/subscriptions/${id}`, { signal })
}

/**
 * Остановить подписку (#94): счета по ней перестают выставляться. Сервер сужает запись
 * грантом `update`, а не чтения.
 */
export function deactivateSubscription(id: string): Promise<Subscription> {
  return request<Subscription>(`${BASE}/subscriptions/${id}/deactivate`, { method: 'POST' })
}

/**
 * Возобновить подписку (#94). Пропущенные за время остановки периоды **не догоняются** —
 * расписание встаёт на ближайший будущий срок; подписку с прошедшей датой окончания сервер
 * не возобновит (`recurring.ended`).
 */
export function reactivateSubscription(id: string, on: string | null): Promise<Subscription> {
  const query = on ? `?on=${encodeURIComponent(on)}` : ''
  return request<Subscription>(`${BASE}/subscriptions/${id}/reactivate${query}`, { method: 'POST' })
}

/** Авансы контрагента: подтверждённые входящие с неразнесённым остатком. */
export function fetchAdvances(counterpartyId: string, signal?: AbortSignal): Promise<Payment[]> {
  return request<Payment[]>(`${BASE}/counterparties/${counterpartyId}/advances`, { signal })
}

// ------------------------------------------------------------------ операции

/**
 * Отправка счёта клиенту.
 *
 * Переводит `draft → sent` и фиксирует момент. Счёт без позиций домен отправить не даст, и
 * предсказывать это здесь не нужно: своя проверка завела бы второе место, где решается,
 * что счёт готов.
 */
export function sendInvoice(invoiceId: string): Promise<Invoice> {
  return request<Invoice>(`${BASE}/invoices/${invoiceId}/send`, { method: 'POST' })
}

/**
 * Позиция черновика счёта (#94).
 *
 * НДС не передаётся: ставку выбирает домен — код ставки товара или стандартная на дату
 * счёта (`InvoiceService.addLineTo`). Цена обязательна: прайс-листы считают цену сделки и
 * КП, а не счёта, и пустая цена означала бы бесплатную строку, а не «по прайсу».
 */
export interface InvoiceLineInput {
  productId: string | null
  description: string
  uomId: string | null
  quantity: number
  unitPrice: number
  discountPercent: number
}

/** Добавить позицию в черновик; ответ — счёт с пересчитанными итогами. */
export function addInvoiceLine(invoiceId: string, line: InvoiceLineInput): Promise<Invoice> {
  return request<Invoice>(`${BASE}/invoices/${invoiceId}/lines`, { method: 'POST', body: line })
}

/** Убрать позицию из черновика. Выставленный счёт домен не правит (`invoice.not_draft`). */
export function removeInvoiceLine(invoiceId: string, lineId: string): Promise<Invoice> {
  return request<Invoice>(`${BASE}/invoices/${invoiceId}/lines/${lineId}`, { method: 'DELETE' })
}

/**
 * Отмена счёта.
 *
 * Причина обязательна: отменённый счёт остаётся в реестре навсегда, и через год «почему
 * его отменили» спросят у того, кто не помнит. Пустую причину домен отвергает — здесь она
 * не подставляется заглушкой.
 */
export function cancelInvoice(invoiceId: string, reason: string): Promise<Invoice> {
  return request<Invoice>(`${BASE}/invoices/${invoiceId}/cancel`, {
    method: 'POST',
    body: { reason },
  })
}

/**
 * Разнесение платежа на счёт.
 *
 * Зовётся со стороны **платежа** (`/payments/{id}/allocations`), потому что так устроен
 * домен: платёж — источник денег, счёт — их назначение. Экран при этом стоит на карточке
 * счёта, и это нормально: направление вызова и направление интерфейса не обязаны
 * совпадать.
 *
 * Повторное разнесение того же платежа на тот же счёт **увеличивает** существующую строку,
 * а не добавляет вторую: пара уникальна, иначе у неё два ответа на вопрос «сколько этого
 * платежа ушло на этот счёт».
 */
export function allocatePayment(
  paymentId: string,
  invoiceId: string,
  amount: number,
): Promise<Allocation> {
  return request<Allocation>(`${BASE}/payments/${paymentId}/allocations`, {
    method: 'POST',
    body: { invoiceId, amount, rule: 'manual' },
  })
}

/** Снятие разнесения: суммы возвращаются и платежу, и счёту. */
export function deallocate(allocationId: string): Promise<{ deallocated: boolean }> {
  return request<{ deallocated: boolean }>(`${BASE}/payments/allocations/${allocationId}`, {
    method: 'DELETE',
  })
}

// ------------------------------------------------------------------ счёт: арифметика

/**
 * Итог одной строки, посчитанный **тем же порядком, что и на сервере**.
 *
 * Существует не ради отображения — итоги приходят с сервера, — а ради **проверки**: см.
 * {@link linesReconcile}. Порядок действий здесь воспроизводит `InvoiceItem.recalculate`
 * дословно, и любое отклонение от него делает проверку ложной.
 *
 * Порядок: цена × количество → минус скидка → НДС на сумму **со скидкой** → округление
 * **построчно** до копеек. НДС, начисленный до скидки, завысил бы налог; округление один
 * раз в конце дало бы итог, не равный сумме строк, которые видит клиент, — а его
 * бухгалтер складывает именно строки.
 */
export function lineArithmetic(line: {
  quantity: number
  unitPrice: number
  discountPercent: number
  vatRate: number
}): { net: number; vat: number; total: number } {
  const gross = line.quantity * line.unitPrice
  const net = round2(gross * (1 - line.discountPercent / 100))
  const vat = round2(net * (line.vatRate / 100))
  return { net, vat, total: round2(net + vat) }
}

/**
 * Сходится ли итог счёта с суммой его строк.
 *
 * Не украшение, а контроль: расхождение здесь означает, что где-то итог посчитан не так,
 * как строки, и клиент, сложивший строки, получит не ту цифру, что стоит в счёте. Молча
 * показать оба числа значит переложить обнаружение на него.
 *
 * Сравнение точное, до копейки: допуск здесь означал бы, что мы заранее согласны на
 * расхождение — и оно вырастет ровно до допуска.
 */
export function linesReconcile(invoice: Invoice): boolean {
  if (!invoice.lines) return true
  const sum = invoice.lines.reduce((acc, line) => acc + line.lineTotal, 0)
  return round2(sum) === round2(invoice.total)
}

/** Сумма разнесений: обязана сойтись с `paidTotal`. */
export function settledTotal(settlements: Settlement[]): number {
  return round2(settlements.reduce((acc, row) => acc + row.allocation.amount, 0))
}

/**
 * Копейки.
 *
 * Через целые, а не `toFixed`: `toFixed` возвращает строку, и цепочка «строка → число →
 * строка» на каждой операции теряет ровно то, ради чего округление и делается.
 */
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

// ------------------------------------------------------------------ платёж (карточка)

export function fetchPayment(id: string, signal?: AbortSignal): Promise<Payment> {
  return request<Payment>(`${BASE}/payments/${id}`, { signal })
}

/**
 * Сделанное разнесение платежа: на какой счёт и сколько. Номер счёта приходит с сервера —
 * он напечатан на платёжке, и право на сам счёт для него не нужно.
 */
export interface PaymentAllocationLine {
  id: string
  invoiceId: string
  invoiceNumber?: string | null
  amount: number
  currency: string
  rule: 'auto' | 'manual'
  allocatedAt: string
  allocatedBy?: string | null
}

/** Разнесения платежа — в его области видимости: чужой платёж — 404, как и его карточка. */
export function fetchPaymentAllocations(id: string, signal?: AbortSignal): Promise<PaymentAllocationLine[]> {
  return request<PaymentAllocationLine[]>(`${BASE}/payments/${id}/allocations`, { signal })
}

/** Подтверждение: неподтверждённый платёж (холд) разносить нельзя. */
export function confirmPayment(id: string): Promise<Payment> {
  return request<Payment>(`${BASE}/payments/${id}/confirm`, { method: 'POST' })
}

/**
 * Автосверка со счетами: номер в назначении, точная сумма, старые счета.
 * `matched: false` — нормальный исход: платёж уходит человеку.
 */
export function autoMatchPayment(id: string): Promise<{ paymentId: string; matched: boolean; allocations: Allocation[] }> {
  return request(`${BASE}/payments/${id}/auto-match`, { method: 'POST' })
}

/**
 * Сторнирование: снимаются все разнесения, платёж переходит в `reversed`. Право `delete`:
 * отменяется зафиксированный денежный факт.
 */
export function reversePayment(id: string): Promise<Payment> {
  return request<Payment>(`${BASE}/payments/${id}/reverse`, { method: 'POST', body: {} })
}
